import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const AuthContext = createContext(null);

function formatError(error) {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(" · ");
  return detail || "Operazione non riuscita";
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const authedRef = useRef(false);

  useEffect(() => {
    authedRef.current = Boolean(session && typeof session === "object");
  }, [session]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && authedRef.current) {
          authedRef.current = false;
          setSession(false);
          toast.error("Sessione scaduta. Rifare il login.");
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const response = await axios.get(`${AUTH_API}/me`, { withCredentials: true });
      setSession(response.data);
    } catch {
      setSession(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = useCallback(async (payload) => {
    const response = await axios.post(`${AUTH_API}/login`, payload, { withCredentials: true });
    await refreshSession();
    return response.data;
  }, [refreshSession]);

  const registerCompany = useCallback(async (payload) => {
    const response = await axios.post(`${AUTH_API}/register-company`, payload, { withCredentials: true });
    setSession(false);
    return response.data;
  }, []);

  const activateInvitation = useCallback(async (payload) => {
    await axios.post(`${AUTH_API}/activate-invitation`, payload, { withCredentials: true });
    await refreshSession();
  }, [refreshSession]);

  const verifyEmail = useCallback((payload) => axios.post(`${AUTH_API}/verify-email`, payload), []);

  const resendVerification = useCallback((payload) => axios.post(`${AUTH_API}/resend-verification`, payload), []);

  const requestPasswordReset = useCallback((payload) => axios.post(`${AUTH_API}/password-reset/request`, payload), []);

  const confirmPasswordReset = useCallback((payload) => axios.post(`${AUTH_API}/password-reset/confirm`, payload), []);

  const confirmEmailChange = useCallback((payload) => axios.post(`${AUTH_API}/profile/confirm-email-change`, payload), []);

  const logout = useCallback(async () => {
    await axios.post(`${AUTH_API}/logout`, {}, { withCredentials: true });
    setSession(false);
  }, []);

  const contextValue = {
    session,
    loading,
    login,
    logout,
    registerCompany,
    activateInvitation,
    verifyEmail,
    resendVerification,
    requestPasswordReset,
    confirmPasswordReset,
    confirmEmailChange,
    refreshSession,
    formatError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("AuthContext mancante");
  return context;
}